/** @jsxImportSource @emotion/react */
import { Table } from "../../../components/table-components/Table";
import { TableRowHeader } from "../../../components/table-components/TableRowHeader";
import { TableCellHeaderText } from "../../../components/table-components/TableCellHeaderText";
import { PageFooter } from "../../../components/page-skeleton/PageFooter";
import { StyledText } from "../../../assets/typography/typography";
import { TableRow } from "../../../components/table-components/TableRow";
import { TableCellText } from "../../../components/table-components/TableCellText";
import { css } from "@emotion/react";
import { colors } from "../../../components/colors";
import { Fragment, MutableRefObject } from "react";
import Sortable from "sortablejs";
import { DragAndDropContainer } from "../../../components/DragAndDropContainer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { toastr } from "react-redux-toastr";
import { getErrorMessage } from "../../../error-handler";
import { useSelector } from "react-redux";
import * as authSelectors from "../../auth/authSelectors";
import { SpinnerBlock } from "../../common/Spinner";
import { ProductTag } from "../model";
import { DropdownItemButton } from "../../common/DropdownItemButton";
import { TableEllipsisDropdown } from "../../../components/TableEllipsisDropdown";
import { ActionSpinner } from "../../../components/ActionSpinner";
import {
  useDeleteProductTag,
  useUpdateProductTagSorting,
} from "../service/product-tags-api";
import { useSearchParamsManipulation } from "../../../hooks/use-searchparams-manipulation";

type Props = {
  productTags: ProductTag[];
};

const sortTableCellStyle = css`
  cursor: grab;
`;

const dragAndDropContainerStyle = css`
  border-bottom: 1px solid ${colors.greyBorder};
`;

export const ProductTagsTable = ({ productTags }: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );

  const { setOption } = useSearchParamsManipulation();

  const {
    mutateAsync: updateSorting,
    isLoading: isUpdatingProductTagsSorting,
  } = useUpdateProductTagSorting();

  const {
    mutateAsync: deleteProductTag,
    isLoading: isDeletingProductTag,
  } = useDeleteProductTag();

  const onDrop = async (
    event: Sortable.SortableEvent,
    containerRef: MutableRefObject<HTMLElement | null>
  ) => {
    if (!containerRef) {
      return;
    }
    // iterating over dom children. Each of them has the id of the category as data attribute
    // @ts-expect-error
    const tags = [...containerRef.current?.children].map((b) => b.dataset.id);
    try {
      await updateSorting({
        data: {
          sort: tags,
        },
        organizationKeyName: organizationKeyName,
      });
      toastr.success("Success", "Product tags reordered successfully");
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  if (isUpdatingProductTagsSorting) {
    return <SpinnerBlock />;
  }

  return (
    <div>
      <Table>
        <thead>
          <TableRowHeader>
            <TableCellHeaderText>Sorting</TableCellHeaderText>
            <TableCellHeaderText>Tag</TableCellHeaderText>
            {/* Hidden until relevant to show tag to end user */}
            {/* <TableCellHeaderText>Display name</TableCellHeaderText> */}
            <th />
          </TableRowHeader>
        </thead>
        <DragAndDropContainer
          isTable={true}
          onEnd={onDrop}
          containerStyle={dragAndDropContainerStyle}
        >
          {productTags
            ?.sort((a, b) => a.sortKey - b.sortKey)
            .map((pt, i) => {
              return (
                <Fragment key={i}>
                  <TableRow data-id={pt.id}>
                    <TableCellText
                      className="pl-5"
                      css={sortTableCellStyle}
                      onClick={() => {
                        setOption(pt.id);
                      }}
                    >
                      <StyledText
                        className="bodyTextRegular mb-0"
                        color={colors.textPrimary}
                      >
                        <FontAwesomeIcon icon={solid("grip-vertical")} />
                      </StyledText>
                    </TableCellText>
                    <TableCellText
                      onClick={() => {
                        setOption(pt.id);
                      }}
                    >
                      <StyledText
                        color={colors.textPrimary}
                        className="bodyTextRegular mb-0"
                      >
                        {pt.name}
                      </StyledText>
                    </TableCellText>
                    {/* Hidden until relevant to show tag to end user */}
                    {/* <TableCellText
                      onClick={() => {
                        setOption(pt.id);
                      }}
                    >
                      <StyledText
                        color={colors.textPrimary}
                        className="bodyTextRegular mb-0"
                      >
                        {pt.title}
                      </StyledText>
                    </TableCellText> */}
                    <TableEllipsisDropdown
                      menu={{
                        items: [
                          {
                            key: "1",
                            label: (
                              <DropdownItemButton
                                onClick={() => {
                                  setOption(pt.id);
                                }}
                                type="button"
                                icon={<FontAwesomeIcon icon={solid("pen")} />}
                                disabled={isDeletingProductTag}
                              >
                                Edit
                              </DropdownItemButton>
                            ),
                          },
                          {
                            key: "2",
                            label: (
                              <DropdownItemButton
                                onClick={async () => {
                                  try {
                                    await deleteProductTag({
                                      data: { id: pt.id },
                                      organizationKeyName,
                                    });
                                    toastr.success(
                                      "Success",
                                      "Product tag deleted"
                                    );
                                  } catch (error: unknown) {
                                    const errorMessage = getErrorMessage(error);
                                    toastr.error("Error", errorMessage);
                                  }
                                }}
                                type="button"
                                icon={
                                  isDeletingProductTag ? (
                                    <ActionSpinner />
                                  ) : (
                                    <FontAwesomeIcon icon={solid("trash")} />
                                  )
                                }
                                disabled={isDeletingProductTag}
                              >
                                Delete product tag
                              </DropdownItemButton>
                            ),
                          },
                        ],
                      }}
                    />
                  </TableRow>
                </Fragment>
              );
            })}
        </DragAndDropContainer>
      </Table>
      <PageFooter>
        <StyledText className="bodyTextRegular mb-0">
          {productTags?.length}{" "}
          {productTags?.length === 1 ? "product tag" : "product tags"} in total
        </StyledText>
      </PageFooter>
    </div>
  );
};
